import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {MatTabChangeEvent, MatTabsModule} from '@angular/material/tabs'; 
import { MatToolbarModule } from '@angular/material/toolbar';
import { NavService } from '@app/services/nav.service';
import { NgxSpinnerService } from 'ngx-spinner';
import PaginationOptions from '@app/models/PaginationOptions';
import * as Global from "@app/global"
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { DatePipe, NgIf } from '@angular/common';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { HelpersService } from '@app/services/helpers.service';
import { ToastService } from '@app/services/toast.service';
import {MatBadgeModule} from '@angular/material/badge';
import { ExpenseService } from '../../../../services/expense.service';

@Component({
  selector: 'expense-activity',
  standalone: true,
  imports: [NgIf,MatTabsModule,MatBadgeModule,FormsModule,ReactiveFormsModule,MatIconModule,MatButtonModule,NgxSkeletonLoaderModule,DatePipe,InfiniteScrollModule],
  templateUrl: './expense-activity.component.html',
  styleUrl: './expense-activity.component.scss'
})
export class ExpenseActivityComponent {
  @Input() data: any = null
  @ViewChild('commentsScroll') commentsScroll!: ElementRef<HTMLDivElement>;
  isLoading:boolean=false
  Global = Global;
  paginationOptions: PaginationOptions;
  log_list: any = [];
  imgErrorIds: Set<any> = new Set();
  ActionType: any = 'Activity';
  formGroup!:FormGroup;
  constructor(
    public fb: FormBuilder,
    public navService: NavService,
    public helpers: HelpersService,
    public toastr: ToastService,
    private spinner: NgxSpinnerService,
    private expenseService: ExpenseService
  ) {
    this.paginationOptions = Global.resetPaginationOptions();
  }
  ngOnChanges(){
    this.formGroup = this.fb.group({
      masterExpenseId: [this.data?.expense_id],
      actionBy: [this.helpers.userId()],
      actionDate: [new Date()],
      remarks: [null,Validators.compose([Validators.required])],
      actionType: ['Remarks'],
      attachment: [null],
      fileName: [null],
    });
    this.fetchExpenseTrackings();

  }
  onTabChanged(event: MatTabChangeEvent) {
    this.paginationOptions = Global.resetPaginationOptions();
    if (event.index==0) {
      this.ActionType="Activity"
    }
    else{
      this.ActionType="Remarks"
    }
    this.fetchExpenseTrackings();
  }
  fetchExpenseTrackings(showSkeleton: boolean = true) {
    const isInitialLoad = this.paginationOptions.skip === 0;
    const isComments = this.ActionType === 'Remarks';
    const showSpinner = isInitialLoad && !showSkeleton;
    if (isInitialLoad && showSkeleton) {
      this.isLoading = true;
    }
    const el = isComments ? this.commentsScroll?.nativeElement : undefined;
    const prevScrollHeight = el?.scrollHeight ?? 0;
    const prevScrollTop = el?.scrollTop ?? 0;
    let params = new URLSearchParams();
    params.set('ActionType', this.ActionType);
    params.set('MasterExpenseId', this.data?.expense_id);
    params.set('pageSize', String(this.paginationOptions.pageSize));
    params.set('skip', String(this.paginationOptions.skip));
    if (showSpinner) {
      this.spinner.show();
    }
    this.expenseService.fetchExpenseTrackings({ params }).subscribe({
      next: (res: any) => {
        this.appendItems(res.data);
        this.paginationOptions = {
          skip: res?.pagingData?.skip ?? 0,
          pageSize: res?.pagingData?.pageSize ?? 10,
          totalCount: res?.pagingData?.totalCount ?? 0,
          totalPages: res?.pagingData?.totalPages ?? 0,
        };
        if (isComments) {
          if (isInitialLoad) {
            this.scrollToBottom();
          } else {
            this.preserveScrollPosition(prevScrollHeight, prevScrollTop);
          }
        }
      },
      error: (err: any) => {
        if (showSpinner) {
          this.spinner.hide();
        }
        this.isLoading = false;
      },
      complete: () => {
        if (showSpinner) {
          this.spinner.hide();
        }
        this.isLoading = false;
      },
    });
  }
  private scrollToBottom() {
    setTimeout(() => {
      const el = this.commentsScroll?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    });
  }
  private preserveScrollPosition(prevScrollHeight: number, prevScrollTop: number) {
    setTimeout(() => {
      const el = this.commentsScroll?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight - prevScrollHeight + prevScrollTop;
      }
    });
  }
  appendItems(items: any = []) {
    if (this.paginationOptions.skip == 0) {
      this.log_list = items;
    } else {
      const existingIds = new Set(this.log_list.map((item: any) => item?.id));
      items.forEach((item: any) => {
        if (!existingIds.has(item?.id)) {
          this.log_list.push(item);
        }
      });
    }
    if (this.ActionType === 'Remarks') {
      this.log_list.sort(
        (a: any, b: any) =>
          new Date(a?.actionDate).getTime() - new Date(b?.actionDate).getTime()
      );
    }
  }
  onImageError(id: any) {
    this.imgErrorIds.add(id);
  }
  onScroll() {
    if (
      this.paginationOptions.totalCount >
      this.paginationOptions.skip + this.paginationOptions.pageSize
    ) {
      this.paginationOptions.skip =
        this.paginationOptions.skip + this.paginationOptions.pageSize;
      this.fetchExpenseTrackings();
    }
  }
  onRemarksAdd(){
    this.formGroup.markAllAsTouched();
    if (this.formGroup.valid) {
      const sentAt = new Date();
      this.formGroup.patchValue({ actionDate: sentAt });
      const optimisticItem = {
        id: 'temp-' + sentAt.getTime(),
        actionByName: this.helpers.userDetails()?.name,
        actionDate: sentAt,
        remarks: this.formGroup.value.remarks,
        imageUrl: null,
        profilePhoto: null,
      };
      this.log_list = [...this.log_list, optimisticItem];
      this.scrollToBottom();
      this.expenseService.addtExpenseRemarks(this.formGroup.value).subscribe({
        next:(res)=>{
          this.formGroup.patchValue({
            remarks:null,
            attachment:null,
            fileName:null,
          })
          this.paginationOptions = {
            ...Global.resetPaginationOptions(),
            pageSize: Math.max(this.paginationOptions.totalCount + 5, 20),
          };
          this.ActionType="Remarks"
          this.fetchExpenseTrackings(false);
          this.formGroup.markAsUntouched();
        },
        error:(err)=>{
          this.log_list = this.log_list.filter(
            (item: any) => item?.id !== optimisticItem.id
          );
        }
      })
    }
  }
}
